// exclude-from-index
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select as MuiSelect
} from '@mui/material';
import { ThemeProvider, StyledEngineProvider, createTheme } from '@mui/material/styles';
import { cloneDeep } from 'lodash';
import { muiGlobalTheme } from '@themes';
import { colors } from '@custom-components';
import { Checkbox } from '../Checkbox';
import { ConfirmationModal } from '../../../Utility/Modal/ConfirmationModal';

const renderOptions = ({ options, native, multiple, onConfirm, modalTitle, selectedValues, icon }) =>
  options.map(({ label, value, isExpired, isDisabled, secondaryText }) => {
    if (native) {
      return (
        <option disabled={isDisabled} key={`opt-${value}`} value={value}>
          {label}
        </option>
      );
    }

    if (multiple) {
      return (
        <MenuItem disabled={isDisabled} key={`opt-${value}`} value={value}>
          <Checkbox
            disabled={isDisabled}
            checked={selectedValues ? selectedValues.indexOf(value) > -1 : false}
            value={value}
          />
          <ListItemText primary={label} />
        </MenuItem>
      );
    }

    if (icon) {
      <MenuItem disabled={isDisabled} key={`opt-${value}`} value={value}>
        <ListItemText
          primary={label}
          secondary={secondaryText}
          onClick={(event) => {
            if (isExpired) event.stopPropagation();
          }}
        />
        {isExpired && (
          <ConfirmationModal icon={icon} handleConfirmation={onConfirm} targetValue={value} title={modalTitle} />
        )}
      </MenuItem>;
    }

    return (
      <MenuItem disabled={isDisabled} key={`opt-${value}`} value={value}>
        {label}
      </MenuItem>
    );
  });

const customizeMuiGlobalTheme = (autoWidth) => {
  const newMuiGlobalTheme = cloneDeep(muiGlobalTheme);

  if (!autoWidth) {
    newMuiGlobalTheme.components.MuiFormControl = {
      styleOverrides: { root: { width: '100%', minWidth: '120px' } }
    };
  }

  newMuiGlobalTheme.palette.text.secondary = colors.colorGrey03;
  newMuiGlobalTheme.palette.text.primary = colors.black;

  newMuiGlobalTheme.components = {
    ...newMuiGlobalTheme.components,
    MuiListItemText: {
      styleOverrides: {
        root: {
          textAlign: 'left'
        },
        primary: {
          color: colors.black
        },
        secondary: {
          color: colors.black
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: colors.black,
          height: '46px',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)'
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)'
            }
          }
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: colors.black
        },
        body1: {
          color: colors.black
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        inputMarginDense: {
          paddingBottom: '10px'
        }
      }
    }
  };
  return createTheme(newMuiGlobalTheme);
};

function Select(props) {
  const {
    errorText,
    helperText,
    autoWidth,
    disabled,
    id,
    error,
    native,
    multiple,
    label,
    name,
    options,
    required,
    value: selectedValues,
    variant,
    icon,
    onConfirm,
    modalTitle,
    onChange,
    onBlur,
    styledProps,
    ...defaults
  } = props;

  const InputLabelRef = useRef();

  /**
   * SU-9: upgrade MUI to v5.0.0
   * When there are no issues for UI, remove the below lines
   */
  // const [_, setLabelWidth] = useState(0);
  // useEffect(() => {
  //   if (InputLabelRef.current) setLabelWidth(InputLabelRef?.current?.offsetWidth);
  //   return () => {};
  // }, [InputLabelRef.current]);

  const selectTheme = useRef(customizeMuiGlobalTheme(autoWidth));

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={selectTheme.current}>
        <FormControl disabled={disabled} error={error} variant={variant} style={{ ...styledProps }}>
          <InputLabel ref={InputLabelRef} htmlFor={label} required={required}>
            {label}
          </InputLabel>
          <MuiSelect
            {...defaults}
            disabled={disabled}
            onChange={onChange}
            id={id}
            error={error}
            native={native}
            multiple={multiple}
            label={label}
            name={name}
            options={options}
            required={required}
            value={selectedValues}
            variant={variant}
            icon={icon}
            onConfirm={onConfirm}
            modalTitle={modalTitle}
            onBlur={onBlur}
            autoWidth={autoWidth}
            inputProps={{ name, id }}
            input={
              variant === 'outlined' ? (
                <OutlinedInput label={label} id={id} name={name} />
              ) : (
                <Input label={label} id={label} name={name} />
              )
            }
            renderValue={
              multiple &&
              ((selected) => selected.map((item) => options.find((opt) => item === opt.value).label).join(', '))
            }
            MenuProps={{ variant: 'menu' }}
          >
            {renderOptions({ options, native, multiple, onConfirm, modalTitle, selectedValues, icon })}
          </MuiSelect>
          {((error && errorText) || helperText) && (
            <FormHelperText>{(error && errorText) || helperText}</FormHelperText>
          )}
        </FormControl>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

Select.propTypes = {
  /**
   * Displays additional text (or replaces helper text) that sits below the select
   */
  errorText: PropTypes.string,
  /**
   * Displays additional text that sits below the select
   */
  helperText: PropTypes.string,
  /**
   * Provides a unique identifier used between the label and select
   */
  id: PropTypes.string.isRequired,
  /**
   * Displays text over the select and acts as a label for the select
   */
  label: PropTypes.string.isRequired,
  /**
   * Select name value
   */
  name: PropTypes.string.isRequired,
  /**
   * isDisabled: disables the use of the option and updates the styles
   * label: Option text displayed in the select dropdown
   * value: Value to return when selected
   */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      isDisabled: PropTypes.bool,
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ).isRequired,
  /**
   * Sets Select width to content width
   */
  autoWidth: PropTypes.bool,
  variant: PropTypes.oneOf(['outlined', 'standard']),
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  native: PropTypes.bool,
  multiple: PropTypes.bool,
  required: PropTypes.bool,
  value: PropTypes.node,
  icon: PropTypes.node,
  onConfirm: PropTypes.func,
  modalTitle: PropTypes.string,
  styledProps: PropTypes.shape({}),
  onChange: PropTypes.func,
  onBlur: PropTypes.func
};

Select.defaultProps = {
  errorText: undefined,
  helperText: undefined,
  autoWidth: false,
  variant: 'standard',
  disabled: false,
  error: false,
  native: false,
  multiple: false,
  required: false,
  value: '',
  icon: '',
  onConfirm: () => {},
  modalTitle: '',
  styledProps: {},
  onChange: () => {},
  onBlur: () => {}
};

export default Select;
